import { iou } from '@/geo/iou'
import { dist, type Poly, type Pt } from '@/geo/types'
import type { ProposalDiff } from '@/schema'
import type { StageBody } from '@/schema'

/**
 * Proposal ↔ document matching (§9.4).
 *
 * The review bar works from this client-side diff against the document **on screen** (which may
 * hold unsaved edits and suggestions already taken), not from the frozen diff the server computed
 * when the proposal was fetched. It also:
 *
 *  - draws the connector between a changed pair on the canvas (it needs the actual geometry on
 *    both sides, not just ids);
 *  - keeps working when a proposal arrives without a `diff` (an old row, or an offline fixture);
 *  - answers "what would happen if I moved the threshold" without a round trip.
 *
 * Rules, as the server's `merge.py` applies them (an item the client offers but the server would
 * leave alone could never be settled — it would sit in the list after every "Keep"):
 *
 *  - a feature first matches the document feature with the **same id** in the same collection
 *    (so a suggestion already taken, which keeps its id, reads as "unchanged"), whatever its kind
 *    — the server decides "same" from the geometry alone;
 *  - the rest match by geometry within their own collection and kind: a region by IoU (≥ 0.95
 *    unchanged, ≥ 0.5 changed, less is new), a spot by its point and a path by its first point
 *    (< 0.5 % unchanged, < 3 % changed);
 *  - a way in/out has no geometry of its own, so the server never matches one by position: a
 *    proposed one on the same edge / the same spot with the same kind as one already there is a
 *    duplicate (not offered); anything else is new. Its spot is compared under the id the spot
 *    ends up with in the document;
 *  - a feature a person made (`provenance.source: 'human'`) or locked is never touched and is
 *    listed as skipped, and a proposed id the document tombstoned (a person deleted it) is never
 *    offered again, not even as a change.
 */

export const IOU_UNCHANGED = 0.95
export const IOU_CHANGED = 0.5
export const ANCHOR_MATCH_PCT = 3
/** A point that moved less than this (percent) is the same point (`merge.py` `_is_same`). */
export const POINT_SAME_PCT = 0.5

export type FeatureCollection = 'region' | 'anchor' | 'portal' | 'path'

export interface MatchedPair {
  /** Id in the current document. */
  current: string
  /** Id in the proposal body. */
  proposed: string
  kind: string
  collection: FeatureCollection
  iou?: number
  dist?: number
  /** Centroid-ish points to draw a connector between. */
  from: Pt
  to: Pt
  status: 'unchanged' | 'changed'
  locked: boolean
}

export interface ClientDiff extends ProposalDiff {
  pairs: MatchedPair[]
}

interface FeatureView {
  id: string
  kind: string
  collection: FeatureCollection
  poly?: Poly
  point?: Pt
  /** Portals: where they are attached (`edge:left`, `anchor:<id>`), for "is it the same one". */
  where?: string
  /** Portals: how characters pass (`walk_off`, `door_open`…), part of "did it change". */
  transition?: string
  /** Portals: the spot they stand on (the proposal's id, before any re-pointing). */
  anchorId?: string
  locked: boolean
}

/** `merge.py` `is_locked`: locked, or made by a person. */
function lockedOf(p: { locked?: boolean; source?: string } | undefined): boolean {
  return p?.locked === true || p?.source === 'human'
}

function viewsOf(body: StageBody): FeatureView[] {
  const out: FeatureView[] = []
  for (const r of body.regions) out.push({ id: r.id, kind: r.kind, collection: 'region', poly: r.poly, locked: lockedOf(r.provenance) })
  for (const a of body.anchors) out.push({ id: a.id, kind: a.kind, collection: 'anchor', point: a.at, locked: lockedOf(a.provenance) })
  for (const p of body.portals) {
    const anchor = p.anchor_id ? body.anchors.find((x) => x.id === p.anchor_id) : undefined
    out.push({
      id: p.id,
      kind: p.kind,
      collection: 'portal',
      point: anchor?.at ?? edgePoint(p.edge),
      where: p.anchor_id ? `anchor:${p.anchor_id}` : `edge:${p.edge ?? ''}`,
      ...(p.anchor_id ? { anchorId: p.anchor_id } : {}),
      ...(p.transition ? { transition: p.transition } : {}),
      locked: lockedOf(p.provenance),
    })
  }
  for (const w of body.paths) out.push({ id: w.id, kind: w.kind, collection: 'path', point: w.points[0], locked: lockedOf(w.provenance) })
  return out
}

function edgePoint(edge: 'left' | 'right' | 'top' | 'bottom' | undefined): Pt | undefined {
  switch (edge) {
    case 'left':
      return [1, 75]
    case 'right':
      return [99, 75]
    case 'top':
      return [50, 1]
    case 'bottom':
      return [50, 99]
    default:
      return undefined
  }
}

function centroidOf(v: FeatureView): Pt {
  if (v.point) return v.point
  const ring = v.poly?.outer ?? []
  if (ring.length === 0) return [50, 50]
  let x = 0
  let y = 0
  for (const p of ring) {
    x += p[0]
    y += p[1]
  }
  return [x / ring.length, y / ring.length]
}

type Score = { iou?: number; dist?: number; status: 'unchanged' | 'changed' }

/** How alike two features are (`merge.py` `_similarity` + `_is_same`); null when there is no geometry to compare. */
function similarity(a: FeatureView, b: FeatureView): Score | null {
  if (a.poly && b.poly) {
    const overlap = iou(a.poly, b.poly)
    return { iou: overlap, status: overlap >= IOU_UNCHANGED ? 'unchanged' : 'changed' }
  }
  if (a.point && b.point) {
    const d = dist(a.point, b.point)
    return { dist: d, status: d < POINT_SAME_PCT ? 'unchanged' : 'changed' }
  }
  return null
}

/** Score a candidate pair found by geometry; `null` when the two cannot be the same feature. */
function score(a: FeatureView, b: FeatureView): Score | null {
  if (a.collection !== b.collection || a.kind !== b.kind) return null
  if (a.collection === 'portal') {
    // No geometry: only an exact duplicate (same kind, same edge / spot) is "the same one".
    return a.where !== undefined && a.where === b.where ? { status: 'unchanged' } : null
  }
  const s = similarity(a, b)
  if (!s) return null
  if (s.iou !== undefined) return s.iou >= IOU_CHANGED ? s : null
  return (s.dist ?? Infinity) < ANCHOR_MATCH_PCT ? s : null
}

/** Two features with the same id: the same feature — would the merge change it? */
function sameIdScore(a: FeatureView, b: FeatureView): Score {
  if (a.collection === 'portal') {
    // The server replaces a same-id way in/out outright; it only "changes" if something differs.
    const same = a.kind === b.kind && a.where === b.where && (a.transition ?? '') === (b.transition ?? '')
    return { status: same ? 'unchanged' : 'changed' }
  }
  return similarity(a, b) ?? { status: 'changed' }
}

/**
 * Match the current document against a proposal: same id first, then greedy best-first geometry,
 * one collection at a time (spots before ways in/out, which stand on them).
 */
export function diffBodies(current: StageBody, proposed: StageBody): ClientDiff {
  const tombstones = new Set(current.tombstones ?? [])
  const lhs = viewsOf(current)
  // A person deleted it: the merge skips it entirely, so it is neither new nor a change.
  const rhs = viewsOf(proposed).filter((b) => !tombstones.has(b.id))

  const usedLeft = new Set<string>()
  const usedRight = new Set<string>()
  const pairs: MatchedPair[] = []
  const keyOf = (v: FeatureView) => `${v.collection}:${v.id}`
  const pair = (a: FeatureView, b: FeatureView, s: Score) => {
    usedLeft.add(keyOf(a))
    usedRight.add(keyOf(b))
    pairs.push({
      current: a.id,
      proposed: b.id,
      kind: a.kind,
      collection: a.collection,
      iou: s.iou,
      dist: s.dist,
      from: centroidOf(a),
      to: centroidOf(b),
      status: a.locked ? 'unchanged' : s.status,
      locked: a.locked,
    })
  }

  const matchCollection = (collection: FeatureCollection, right: FeatureView[]) => {
    const left = lhs.filter((a) => a.collection === collection)
    // 1. The same id in the same collection is the same feature.
    const byId = new Map(left.map((a) => [a.id, a]))
    for (const b of right) {
      const a = byId.get(b.id)
      if (a) pair(a, b, sameIdScore(a, b))
    }
    // 2. Everything else by geometry, within the collection and the kind.
    const candidates: { a: FeatureView; b: FeatureView; rank: number; s: Score }[] = []
    for (const a of left) {
      if (usedLeft.has(keyOf(a))) continue
      for (const b of right) {
        if (usedRight.has(keyOf(b))) continue
        const s = score(a, b)
        if (!s) continue
        candidates.push({ a, b, rank: s.iou ?? (s.dist !== undefined ? 1 - s.dist / ANCHOR_MATCH_PCT : 1), s })
      }
    }
    candidates.sort((p, q) => q.rank - p.rank)
    for (const c of candidates) {
      if (usedLeft.has(keyOf(c.a)) || usedRight.has(keyOf(c.b))) continue
      pair(c.a, c.b, c.s)
    }
  }

  const of = (collection: FeatureCollection) => rhs.filter((b) => b.collection === collection)
  matchCollection('region', of('region'))
  matchCollection('anchor', of('anchor'))
  matchCollection('path', of('path'))
  // A proposed way in/out names the proposal's spot; after a merge it stands on whatever id that
  // spot has in the document (the matched one's, or its own when it is new).
  const spotIn = new Map(pairs.filter((p) => p.collection === 'anchor').map((p) => [p.proposed, p.current]))
  matchCollection(
    'portal',
    of('portal').map((b) => (b.anchorId && spotIn.has(b.anchorId) ? { ...b, where: `anchor:${spotIn.get(b.anchorId)}` } : b)),
  )

  const changed = pairs.filter((p) => p.status === 'changed' && !p.locked).map((p) => ({ id: p.proposed, ...(p.iou !== undefined ? { iou: p.iou } : {}), ...(p.dist !== undefined ? { dist: p.dist } : {}) }))
  const unchanged = pairs.filter((p) => p.status === 'unchanged' && !p.locked).map((p) => p.proposed)
  const added = rhs.filter((b) => !usedRight.has(keyOf(b))).map((b) => b.id)
  // A feature in the document that the proposal has no opinion about is not "removed" — a re-run
  // only ever proposes what it saw.
  const removed: string[] = []
  const lockedSkipped = pairs.filter((p) => p.locked).map((p) => p.current)

  return { added, changed, unchanged, removed, locked_skipped: lockedSkipped, pairs }
}

/** Confidence of one proposed feature, for the threshold slider. */
export function confidenceOf(body: StageBody, id: string): number {
  const all = viewsOfWithProvenance(body)
  return all.get(id) ?? 0
}

function viewsOfWithProvenance(body: StageBody): Map<string, number> {
  const map = new Map<string, number>()
  for (const r of body.regions) map.set(r.id, r.provenance.confidence)
  for (const a of body.anchors) map.set(a.id, a.provenance.confidence)
  for (const p of body.portals) map.set(p.id, p.provenance.confidence)
  for (const w of body.paths) map.set(w.id, w.provenance.confidence)
  for (const l of body.layers) map.set(l.id, l.provenance.confidence)
  return map
}

/** Ids a "accept all ≥ threshold" click would take. */
export function idsAboveThreshold(body: StageBody, diff: ProposalDiff, threshold: number): string[] {
  const conf = viewsOfWithProvenance(body)
  const candidates = [...diff.added, ...diff.changed.map((c) => c.id)]
  return candidates.filter((id) => (conf.get(id) ?? 0) >= threshold)
}

/** Geometry of one proposed feature, for the ghost layer. */
export function proposedPolys(body: StageBody, ids: readonly string[]): { id: string; kind: string; poly: Poly; confidence: number }[] {
  const wanted = new Set(ids)
  return body.regions
    .filter((r) => wanted.size === 0 || wanted.has(r.id))
    .map((r) => ({ id: r.id, kind: r.kind, poly: r.poly, confidence: r.provenance.confidence }))
}
